"use client";
import _ from "lodash";
import React, { useEffect, useState } from 'react';
import "@/app/style/racingStatistic.scss";
import styled from "styled-components";
import { useLanguage } from "@/app/utils/LanguageContext";
import { useMyContext } from "@/app/utils/MyContext";
import NoInformation from "@/app/ui/Common/NoInformation";
import SinglePhoto, { generateSingleImgData } from "@/app/ui/PhotosViewer/singlePhoto";
import VideoPlayerIcon from "@/app/ui/Common/SvgComponent/VideoPlayerIcon";
import VideoPlayer from "@/app/ui/Common/VideoPlayer";
import { ValidVideo, VideoSource } from "@/app/utils/class/common/video";
import { generateVideoUrl, generateVideoSource, validateVideo } from "@/app/utils/Video";
import { getRacingStatistics } from '@/app/utils/request/requestList';
import { PlainTextTable } from "@/app/utils/class/cms";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { textSize, text_size } from "@/app/utils/MixinSize";
import { TableHead } from "@mui/material";
import { defaultValue, generateAdobeDataRaceNo, setRacesDetailLoading } from "@/app/utils/Common";
import { VideoWAData } from "@/app/ui/Common/VideoPlayer/player";

interface propType {
  setRacesDetailLoading: (loading: boolean) => void;
    type?: string;
}

const Container = styled.div` 
    background-color: white;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    border-radius: 0px 0px 4px 4px;
`;

const TabTitle = styled.div`
  width: 100%;
  height: 36px;
  line-height: 16px;
  padding: 10px 12px;
  ${text_size(15)}
  font-weight: 500;
  background-color: #EDEDED;
  box-shadow: 0px 2px 10px 0px rgb(0 0 0 / 10%);
`;

const ElementContainer = styled("div")<{ $isMobile?: boolean, $assetPrefix?: string  }>(({ $isMobile, $assetPrefix }) => ({
    width: "100%",
    boxShadow: "0px 2px 10px 0px #0000001A",
    "> div > img": {
        width: "100%",
    },
    ".sectionContent img": {
        maxWidth: "100%",
    },
    backgroundImage: `url(${$assetPrefix}/text_bg.png)`,
}));

const DivVideo = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const VideoContainer = styled.div`
    display: flex;
    padding: 0 12px 12px 12px;
`;

const DivVideoPlayIcon = styled.div`
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    position: absolute;
    display: flex;

    svg {
        width: 38px;
        height: 38px;
    }
`;

const ImgVideo = styled.img`
    width: 100%;
    display: block;
    margin: 0 auto;
`;

const StyledTableHeadCell = styled(TableCell)<{ $isMobile?: boolean }>(({ $isMobile }) => ({
    [`&.${tableCellClasses.root}`]: {
        ...textSize(11),
        fontWeight: 400,
        height: "32px",
        padding: $isMobile ? "8px 8px 8px 12px" : "4px 8px",
        lineHeight: "inherit",
        backgroundColor: "var(--palette-grey-300)"
    },
}));

const StyledTableHeadRow = styled(TableHead)(() => ({
    'th: first-child': {
        paddingRight: "8px",
        paddingLeft: "15px",
    }
}));

const StyledTableCell = styled(TableCell)<{ $isMobile?: boolean }>(({$isMobile}) => ({
    [`&.${tableCellClasses.root}`]: {
        color: "#333333",
        ...($isMobile ? 
            {
                whiteSpace: "nowrap",
            }:
            {
                whiteSpace: "pre-line",
                wordBreak: "break-word",
                hyphens: "auto",
            }
        )
    },
    [`&.${tableCellClasses.body}`]: {
        color: "#333333",
        ...textSize(13),
        fontWeight: 500,
        padding: "4px 0px 4px 8px",
        verticalAlign: "baseline",
        textAlign: "left",
        height: "32px",
        lineHeight: "24px"
    },
}));

const StyledTableRow = styled(TableRow)(() => ({
    '&:nth-of-type(even)': {
        backgroundColor: "#F4F4F4",
    },
    '&:nth-of-type(odd)': {
        backgroundColor: "#FFFFFF",
    },
    'td, th': {
        border: 0,
    },
    'td:last-child, th:last-child': {
        paddingRight: "8px",
    },
    'td: first-child': {
        width: "14%",
        paddingRight: "8px",
        paddingLeft: "15px"
    }
}));

const StyledTableFirstCell = styled(TableCell)`
  &.${tableCellClasses.head} {
    background-color: rgba(225, 225, 225, 1) !important;
    ${text_size(11)}
    font-weight: 500;
    color: rgba(0, 0, 0, 1);
    width: 105px;
    min-width: 105px;
    max-width: 105px;
    padding: 0 8px;
    position: sticky !important;
    position: -webkit-sticky;
    left: 0;
    top: 0;
    z-index: 2;
    background: inherit;
  }
  &.${tableCellClasses.body} {
    width: 105px;
    min-width: 105px;
    max-width: 105px;
    ${text_size(13)}
    font-weight: 500;
    color: rgba(0, 0, 0, 1);
    padding: 8px;
    vertical-align: top;
    position: sticky !important;
    position: -webkit-sticky;
    left: 0;
    z-index: 2;
    transform: translateZ(0);
    background: inherit;
  }
`;

const RacingStatistic: React.FC<propType> = (props: propType) => {
    const { state: myContextState } = useMyContext();
    const [data, setData] = useState<any>([]);
    const { state: lang } = useLanguage();
    const { t } = lang;
    const [selectedImage, setSelectedImage] = useState({});
    const [isOpenImage, setIsOpenImage] = useState(false);
    const [isShowReplay, setIsShowVideoPlay] = useState(false);
    const [replaySources, setVideoSources] = useState([] as VideoSource[]);
    const [WAData, setWAData] = useState<VideoWAData|undefined>(undefined);

    const isMobile = props.type == "mobile";
    const expectedMinCol = 4;

    useEffect(() => {
        if (myContextState.isLoadingRaces) return;
        if (!myContextState.selectedMeeting?.displayName) {
            setRacesDetailLoading(props.setRacesDetailLoading)
            return 
        }
        getRacingStatistics(lang.langCode, myContextState.season, myContextState.selectedMeeting?.displayName)
        .then(async (res: any) => {
            console.log('racing stat', res)
            if(res?.item?.children)
            {
                const data = await Promise.all<Promise<any>[]>(res?.item?.children?.map(async (item:any, index:number) => {
                    const templateName = item?.template?.name;
                    if(templateName=="RacingStatisticsText")
                    {
                        return {
                            type: "Text",
                            title: item.title?.value,
                            subTitle: item.subTitle?.value,
                            content: item.text?.rendered,
                            isEmpty: !(item.title?.value?.length || item.subTitle?.value?.length || item.text?.rendered?.length)
                        }
                    }else if(templateName=="RacingStatisticsTable")
                    {
                        return {
                            type: "Table",
                            title: item.title?.value,
                            subTitle: item.subTitle?.value,
                            tableData: handedData(item.table?.value),
                            remarks: item.remarks?.value,
                            isEmpty: !(item.title?.value?.length || item.subTitle?.value?.length || item.table?.value?.length || item.remarks?.value?.length)
                        }
                    }else if(templateName=="RacingStatisticsImage")
                    {
                        return {
                            type: "Image",
                            title: item.title?.value,
                            subTitle: item.subTitle?.value,
                            imgPath: item.imageUrl?.src,
                            isEmpty: !(item.title?.value?.length || item.subTitle?.value?.length || item.imageUrl?.src?.length)
                        }
                    }else if(templateName=="RacingStatisticsVideo")
                    {
                        const videoUrl = item.video?.value ? generateVideoUrl(item.video?.value) : null;
                        const videoValidation = await validateVideo(videoUrl);
                        return {
                            type: "Video",
                            title: item.title?.value,
                            subTitle: item.subTitle?.value,
                            thumbnail: item.thumbnail?.src,
                            videoUrl: videoUrl,
                            validation: videoValidation,
                            description: item.description?.value,
                            isEmpty: !(item.title?.value?.length || item.subTitle?.value?.length || item.thumbnail?.src?.length || item.description?.value?.length || videoUrl)
                        }
                    }
                }));
                setData(data);
            }else{
                setData(null);
            }
        })
        .catch((e: any) => {
          console.log(e);
        }).finally(() => {
            setRacesDetailLoading(props.setRacesDetailLoading)
        });
    }, [myContextState.selectedMeeting, myContextState.isLoadingRaces]);

    const handedData = (data: any) => {
        const tblData = new PlainTextTable(data);
        return { headerData: tblData?.headers || [], bodyData: tblData?.contents?.byRow || [] };
    }

    const openPhotoViewer = (e: any) => {
        setSelectedImage(generateSingleImgData(e.target.src));
        setIsOpenImage(true);
    }

    const onCloseImage = () => {
        setIsOpenImage(false);
        setSelectedImage({});
    }

    const handleClose = () => {
        setIsShowVideoPlay(false);
        setWAData(undefined);
        setVideoSources([]);
    }

    const openVideoPlayer = (validVideo: ValidVideo, url: any, waContentTitle?: string) => {
        if (!validVideo.hasVideo()) return;
        setWAData({
          raceNo: generateAdobeDataRaceNo(myContextState.selectedMeetingDate, myContextState.selectedMeeting?.meetingVenueCode, myContextState.selectedRaceNo.valueOf()),
          contentTitle: waContentTitle,
        });
        setVideoSources(generateVideoSource(url.sd, url.hd));
        setIsShowVideoPlay(true);
    }

    const CarnivalPhotoViewer = (props: any) => (<SinglePhoto type="racingStat" disableCaption {...props} />)

    const AutoColumns = (props: {
        expCol: number,
        extCol?: number,
        isHeader?: boolean,
        isColgroup?: boolean,
    }) => {
        const { expCol, extCol = 0, isHeader, isColgroup } = props
        if (isColgroup) {
            return (
                <colgroup>
                    {
                        new Array(expCol).fill("").map((_: any, idx:number)=>
                            <col width={`${100/expCol}%`} key={idx} />
                        )
                    }
                </colgroup>
            )
        }
        return extCol < expCol && (
            new Array(expCol - extCol)?.fill("").map((col, index) => (
                isHeader ? <StyledTableHeadCell key={`placeholder_${index}`} /> : <StyledTableCell key={`placeholder_${index}`} />
            ))
        )
      }

    const renderContent = (isMobile?: boolean) => {
        const contents: any[] = data?.map((d: any, idx: number) => {
            switch (d?.type) {
                case "Text":
                    if (d.isEmpty) return;
                    return <ElementContainer id={`racingStat_Text_${idx}`} $assetPrefix={myContextState.assetPrefix}>
                        <div id="sectionTitle" className='sectionTitle'>{d.title}</div>
                        {d.subTitle?.length ? <div id="subTitle" className="subTitle">{d.subTitle}</div> : <div style={{ height: "12px" }}></div>}
                        <div id="sectionContent" className='sectionContent'><div dangerouslySetInnerHTML={{ __html: d.content }}></div></div>
                    </ElementContainer>
                case "Table":
                    if (d.isEmpty) return;
                    const isTblEvenlySpaced = !isMobile && d?.tableData?.headerData?.length > expectedMinCol
                    const isTblAutoWidened = !isMobile && !isTblEvenlySpaced
                    return <ElementContainer id={`racingStat_Table_${idx}`}>
                        <div id="sectionTitle" className='sectionTitle'>{d.title}</div>
                        {d.subTitle?.length ? <div id="subTitle" className="subTitle">{d.subTitle}</div> : <></>}
                        <div>
                            {
                                d?.tableData?.bodyData.length > 0 ?
                                    <>
                                        <TableContainer id="racingStatTable">
                                            <Table aria-label="simple table" sx={{...(isTblEvenlySpaced&&{tableLayout: 'fixed'})}}>
                                                {
                                                    isTblAutoWidened &&
                                                    <AutoColumns expCol={expectedMinCol} isColgroup />
                                                }
                                                <StyledTableHeadRow id={`racing_stat_table_header_${idx}`}>
                                                    <tr>
                                                        {d?.tableData?.headerData?.map((header: any, index: number) => (
                                                            isMobile && index == 0 ? <StyledTableFirstCell key={`rs_header_${index}`}>{header}</StyledTableFirstCell> :
                                                                <StyledTableHeadCell key={`rs_header_${index}`} colSpan={1}>{header}</StyledTableHeadCell>
                                                        ))}
                                                        {
                                                            isTblAutoWidened &&
                                                            <AutoColumns expCol={d?.tableData?.headerData?.length > 0 ? expectedMinCol : 0} extCol={defaultValue(d?.tableData?.headerData?.length - 1, 0)} isHeader />
                                                        }
                                                    </tr>
                                                </StyledTableHeadRow>
                                                <TableBody>
                                                    {d.tableData?.bodyData?.map((row: any, index: number) => (
                                                        <StyledTableRow key={`row_${index}`} id={`row_${index}`}>
                                                            {
                                                                row?.map((val: any, i: number) => (
                                                                    isMobile && i == 0 ? <StyledTableFirstCell key={`body_row_${i}`}>{val}</StyledTableFirstCell> :
                                                                        <StyledTableCell $isMobile={isMobile} key={`body_row_${i}`} colSpan={1}>
                                                                            {val}
                                                                        </StyledTableCell>
                                                                ))
                                                            }
                                                            {
                                                                isTblAutoWidened &&
                                                                <AutoColumns expCol={expectedMinCol} extCol={defaultValue(row?.length - 1, 0)} />
                                                            }
                                                        </StyledTableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <div className="tableRemarks">{d.remarks}</div>
                                    </>
                                    : <></>
                            }
                        </div>
                    </ElementContainer>
                case "Image":
                    if (d.isEmpty) return;
                    return <ElementContainer id={`racingStat_Image_${idx}`} style={{ boxShadow: "none" }} >
                        <div id="sectionTitle" className='sectionTitle'>{d.title}</div>
                        {d.subTitle?.length ? <div id="subTitle" className="subTitle shadowBox">{d.subTitle}</div> : <></>}
                        <div><img style={{ borderRadius: "0 0 4px 4px", display: "block" }} className="clickable" src={d.imgPath} onClick={openPhotoViewer}></img></div>
                    </ElementContainer>
                case "Video":
                    if (d.isEmpty) return;
                    if (!d.validation.hasVideo() && !d.thumbnail) return;
                    return <ElementContainer id={`racingStat_Video_${idx}`} $assetPrefix={myContextState.assetPrefix}>
                        <div id="sectionTitle" className='sectionTitle'>{d.title}</div>
                        {d.subTitle?.length ? <div id="subTitle" className="subTitle">{d.subTitle}</div> : <div style={{ height: "12px" }}></div>}
                        <VideoContainer>
                            <DivVideo>
                                <div style={{ maxWidth: "854px" }} >
                                    <div id="videoPlayer" style={{ position: "relative", cursor: "pointer" }} onClick={() => openVideoPlayer(d.validation, d.videoUrl, d.title)}>
                                        <ImgVideo src={d.thumbnail} alt="" />
                                        <DivVideoPlayIcon id="videoPlayerIcon"><VideoPlayerIcon /></DivVideoPlayIcon>
                                    </div>
                                    <div id="videoRemarks" className="videoRemarks">
                                        {d.description}
                                    </div>
                                </div>
                            </DivVideo>
                        </VideoContainer>
                    </ElementContainer>
                default:
                    return <></>;
            }
        })
        return <>{...contents}</>
    }

    return (
        <>
            <CarnivalPhotoViewer isOpen={isOpenImage} data={selectedImage} onRequestClose={onCloseImage} />
            <VideoPlayer WAData={WAData} sources={replaySources} open={isShowReplay} isMobile={isMobile} handleClose={handleClose} width={isMobile ? undefined : '60%'} height={isMobile ? undefined : '60%'} showCurrentTooltip showHoveredTooltip autoPlay />
            {props.type === "pc" && <TabTitle id="racingStatTabTitle">{t("racingStat_title")}</TabTitle>}
            {data && data.length ?
                props.type === "pc" ? (
                    <>
                        <Container>
                            {renderContent()}
                        </Container>
                    </>
                ) : (
                    <>
                        <Container>
                            {renderContent(true)}
                        </Container>
                    </>
                )
                : <NoInformation />}
        </>

    )
}

export default RacingStatistic;